import React, { useEffect, useRef, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import Paper from "@mui/material/Paper";
import MatrixInteractionRow from "./MatrixInteractionRow";
import { useStoreDispatch, useStoreSelector } from "../../../store/Hooks";
import Interaction from "../../../models/interactions/Interaction";
import InteractionNode from "../../../models/interactions/InteractionNode";
import InteractionNodeInterval from "../../../models/interactions/InteractionNodeInterval";
import MatrixIntervalControlsRow, {
  CreateNewInteractionMatrixColumnReturnProps,
} from "./MatrixIntervalControlsRow";
import SchedulingMatrixRow from "../../../models/communications/SchedulingMatrixRow";
import InteractionIntervalSetting from "../../../models/communications/InteractionIntervalSetting";
import CommunicationSet from "../../../models/communications/CommunicationSet";
import interactionParentInterval from "../../../models/interactions/InteractionParentInterval";
import {
  compareInteractionNodesByOrdinality,
  compareIntervals,
  getRandomInt,
  interactionsNodesHandleFetch,
  stateDeepCopy,
} from "../../../helpers/DataUpdateHelper";
import InsertionPoint from "../../InsertionPoint";
import InteractionSettings from "../../../models/config/InteractionSettings";
import useHttp from "../../../hooks/useHttp";
import { interactionTreeSliceActions } from "../../../store/slices/InteractionTreeSlice";
import { navSliceActions } from "../../../store/slices/NavSlice";
import Interval from "../../../models/communications/Interval";
import IntervalSet from "../../../models/communications/IntervalSet";
import MatrixCommSetsControlsRow from "./MatrixCommSetsControlsRow";
import MatrixTopControlsRow from "./MatrixTopControlsRow";
import IntervalSetSaveButton from "./controls/widgets/IntervalSetSaveButton";
import MatrixBottomControlsRow from "./MatrixBottomControlsRow";

//interaction_nodes, interactions, and interaction_node_intervals are first pulled in. From interaction_node_intervals, we pull intervals
//from intervals, we pull which sets need to have accordions at this level
// generate a scheduling matrix for each provided intervalSetID
interface SchedulingMatrixProps {
  intervalSetID: number;
  editMode: boolean;
  intervalSet: IntervalSet;
  intervalSetName: string | null;
  currentParent: number; // this will be a pathway
  currentSet: number;
  childInteractions: Array<any>;
  associatedIntervals: Array<Interval>;
  associatedCommSets: Array<CommunicationSet>;
  associatedInteractionNodeIntervals: Array<InteractionNodeInterval>;
  associatedInteractionNodes: Array<InteractionNode>;
  associatedInteractions: Array<Interaction>;
  interactionParentIntervals: Array<interactionParentInterval>;
  intervalSetSaveHandler: any;
}

const SchedulingMatrix = (props: SchedulingMatrixProps) => {
  // detecting changes and reveal save button logic should be handled at this level
  // since the component above this one is responsible for loading in interactions
  // and thus will naturally experience an additional re-render cycle which would affect this
  const [lastSavedItems, setLastSavedItems] = useState(
    props.associatedInteractionNodeIntervals,
  );
  const [changesDetected, setChangesDetected] = useState<boolean>(false);

  const interactionNodeIntervalsToDelete = useStoreSelector(
    (state) => state.interactionTree.InteractionNodeIntervalsToDelete,
  );

  useEffect(() => {
    // Check if current items array is different from the last saved items array
    const itemsHaveChanged =
      JSON.stringify(props.associatedInteractionNodeIntervals) !==
      JSON.stringify(lastSavedItems);
    // Get the array of deleted objects
    // setDeletedObjects(findDeletedObjects(lastSavedItems, props.associatedInteractionNodeIntervals));
    // dispatch(interactionTreeSliceActions.setInteractionNodeIntervalsToDelete({InteractionNodeIntervalsToDelete:deletedObjects}))
    // console.log(interactionNodeIntervalsToDelete)
    const arrayMap = interactionNodeIntervalsToDelete;
    // Array.from(interactionNodeIntervalsToDelete.values())
    //console.log(arrayMap)
    setChangesDetected(itemsHaveChanged);
  }, [props.associatedInteractionNodeIntervals, lastSavedItems]);

  const intervalSetSaveHandler = () => {
    setChangesDetected(false);
    props.intervalSetSaveHandler();
  };

  // Create and add initial ordinal/insertion points

  let matrixInsertionPointFirstRow: SchedulingMatrixRow;
  // if there's already childinteractions, use that data for insertion points
  // otherwise generate new
  if (props.childInteractions.length > 0) {
    matrixInsertionPointFirstRow = new SchedulingMatrixRow(
      props.childInteractions[0].props.matrixRow.interaction,
      props.childInteractions[0].props.matrixRow.interaction_node,
      props.childInteractions[0].props.matrixRow.intervalSettings,
      props.childInteractions[0].props.matrixRow.condition_id,
      props.intervalSetID,
      0, // ordinal
      false,
      null,
      true,
      Interaction.addNewInteractionToIntervalSet,
    );
  } else {
    const interactionID = getRandomInt(0, 9000);
    const interactionNodeID = getRandomInt(9000, 10000000);
    // create intervalSettings array from props.associatedIntervals
    const interaction_x_interval_settings: Array<InteractionIntervalSetting> =
      [];
    props.associatedIntervals.forEach((associatedInterval: Interval) => {
      const interactionIntervalSetting = new InteractionIntervalSetting(
        -1,
        interactionID,
        interactionNodeID,
        associatedInterval.id,
        true,
        null,
        null,
        "",
        props.editMode || false,
      );
      interaction_x_interval_settings.push(interactionIntervalSetting);
    });

    matrixInsertionPointFirstRow = new SchedulingMatrixRow(
      new Interaction(
        interactionID,
        "placeholderInteraction",
        new InteractionSettings("cs1survey"),
        "",
      ),
      new InteractionNode(
        interactionNodeID,
        props.currentSet,
        props.currentParent,
        0,
        12345,
        null,
        null,
        "",
      ),
      interaction_x_interval_settings,
      null,
      props.intervalSetID,
      0, // ordinal
      false,
      null,
      true,
      Interaction.addNewInteractionToIntervalSet,
    );
  }

  const childInteractionsAndInsertionPoints = [];
  // TODO - add first insertion point right away
  const matrixInsertionPointFirstRowComponent = (
    <MatrixInteractionRow
      key={
        matrixInsertionPointFirstRow.interaction_node.id +
        getRandomInt(0, 10000000)
      }
      {...matrixInsertionPointFirstRow}
      editMode={props.editMode}
      // intervalSetName={props.intervalSetName}
      // insertionPoint={allInteractionRow.insertionPoint}
      // intervalSetID = {props.intervalSetID}
    />
  );
  childInteractionsAndInsertionPoints.push(
    matrixInsertionPointFirstRowComponent,
  );
  for (let i = 0; i < props.childInteractions.length; i++) {
    const thisChildInteraction = props.childInteractions[i];
    childInteractionsAndInsertionPoints.push(thisChildInteraction); // add interaction to array

    // create struct for insertion point
    const matrixInsertionPoint = new SchedulingMatrixRow(
      thisChildInteraction.props.matrixRow.interaction,
      thisChildInteraction.props.matrixRow.interaction_node,
      thisChildInteraction.props.matrixRow.intervalSettings,
      thisChildInteraction.props.matrixRow.condition_id,
      thisChildInteraction.props.matrixRow.intervalSetID,
      thisChildInteraction.props.matrixRow.ordinal
        ? thisChildInteraction.props.matrixRow.ordinal
        : 1,
      false,
      null,
      true,
      Interaction.addNewInteractionToIntervalSet,
    );

    const newInsertionPoint = (
      <MatrixInteractionRow
        key={
          matrixInsertionPoint.interaction_node.id + getRandomInt(0, 10000000)
        }
        {...matrixInsertionPoint}
        editMode={props.editMode}
        // intervalSetName={props.intervalSetName}
        // insertionPoint={allInteractionRow.insertionPoint}
        // intervalSetID = {props.intervalSetID}
      />
    );

    childInteractionsAndInsertionPoints.push(newInsertionPoint);
  }
  const associatedIntervalsWithCommSets = props.associatedIntervals.filter(
    (associatedInterval: Interval) => {
      return (
        associatedInterval.communication_set_id !== -1 &&
        associatedInterval.communication_set_id !== null
      );
    },
  );

  return (
    <TableContainer component={Paper}>
      <Table sx={{ minWidth: 700 }} aria-label="customized table">
        {/* handle controls and intervalNames */}
        <TableHead sx={{ textAlign: "right" }}>
          {/* controls for intervals */}
          <MatrixTopControlsRow
            intervalSetID={props.intervalSetID}
            associatedIntervals={props.associatedIntervals}
            associatedInteractionNodeIntervals={
              props.associatedInteractionNodeIntervals
            }
            addNewIntervalToIntervalSet={Interval.addNewInterval}
            associatedCommSets={props.associatedCommSets}
            associatedInteractionNodes={props.associatedInteractionNodes}
            associatedInteractions={props.associatedInteractions}
            currentParent={props.currentParent}
            currentSet={props.currentSet}
            interactionParentIntervals={props.interactionParentIntervals}
            editMode={props.editMode}
            intervalSetName={props.intervalSetName}
            intervalSet={props.intervalSet}
            changesDetected={changesDetected}
            intervalSetSaveHandler={intervalSetSaveHandler}
            // scrollPosition={buttonRight}
          />
          {associatedIntervalsWithCommSets.length > 0 && (
            <MatrixCommSetsControlsRow
              intervalSetID={props.intervalSetID}
              associatedIntervals={props.associatedIntervals}
              associatedInteractionNodeIntervals={
                props.associatedInteractionNodeIntervals
              }
              addNewIntervalToIntervalSet={Interval.addNewInterval}
              associatedCommSets={props.associatedCommSets}
              associatedInteractionNodes={props.associatedInteractionNodes}
              associatedInteractions={props.associatedInteractions}
              currentParent={props.currentParent}
              currentSet={props.currentSet}
              interactionParentIntervals={props.interactionParentIntervals}
              editMode={props.editMode}
              intervalSetName={props.intervalSetName}
              intervalSet={props.intervalSet}
              changesDetected={changesDetected}
              intervalSetSaveHandler={intervalSetSaveHandler}
              // scrollPosition={buttonRight}
            />
          )}
          {/* display the intervals */}
          {props.associatedIntervals.length > 0 && (
            <MatrixIntervalControlsRow
              intervalSetID={props.intervalSetID}
              associatedIntervals={props.associatedIntervals}
              associatedInteractionNodeIntervals={
                props.associatedInteractionNodeIntervals
              }
              associatedCommSets={props.associatedCommSets}
              associatedInteractionNodes={props.associatedInteractionNodes}
              associatedInteractions={props.associatedInteractions}
              currentParent={props.currentParent}
              currentSet={props.currentSet}
              interactionParentIntervals={props.interactionParentIntervals}
              editMode={props.editMode}
              intervalSetName={props.intervalSetName}
              intervalSet={props.intervalSet}
              changesDetected={changesDetected}
              addNewIntervalToIntervalSet={Interval.addNewInterval}
              intervalSetSaveHandler={intervalSetSaveHandler}
              // scrollPosition={buttonRight}
            />
          )}
        </TableHead>
        {/* handle interactions and settings for each interval_x_interaction */}
        {/* create "rows" which represent the interaction and all of its settings */}
        <TableBody>{childInteractionsAndInsertionPoints}</TableBody>
        <MatrixBottomControlsRow
          intervalSetID={props.intervalSetID}
          associatedIntervals={props.associatedIntervals}
          associatedInteractionNodeIntervals={
            props.associatedInteractionNodeIntervals
          }
          associatedCommSets={props.associatedCommSets}
          associatedInteractionNodes={props.associatedInteractionNodes}
          associatedInteractions={props.associatedInteractions}
          currentParent={props.currentParent}
          currentSet={props.currentSet}
          interactionParentIntervals={props.interactionParentIntervals}
          editMode={props.editMode}
          intervalSetName={props.intervalSetName}
          intervalSet={props.intervalSet}
          changesDetected={changesDetected}
          addNewIntervalToIntervalSet={Interval.addNewInterval}
          intervalSetSaveHandler={intervalSetSaveHandler}
          // scrollPosition={buttonRight}
        />
      </Table>
    </TableContainer>
  );
};

export default SchedulingMatrix;
